import React from "react";
import Select from "react-select";

const getOptionLabel = (option) => {
  if (!option || typeof option !== "object") {
    return "";
  }

  return String(
    option.label ??
      option.name ??
      option.nombre ??
      option.company_name ??
      option.full_name ??
      option.number ??
      option.title ??
      ""
  );
};

const getOptionValue = (option) => {
  if (!option || typeof option !== "object") {
    return "";
  }

  return option.value ?? option.id ?? "";
};

const CustomSelect = ({
  value,
  name,
  options = [],
  handleChange,
  onChange,
  label,
  labelClassName = "text-xs font-medium text-cs-dark",
  className = "",
  error,
  holder,
  colorOption,
  ...props
}) => {
  const hasError = Boolean(error);
  const isDisabled = Boolean(props.disabled || props.isDisabled);

  const formattedOptions = Array.isArray(options)
    ? options.map((option) => ({
        ...option,
        value: getOptionValue(option),
        label: getOptionLabel(option),
      }))
    : [];

  const selectedOption =
    formattedOptions.find((option) => String(option.value) === String(value ?? "")) ||
    null;

  const handleOnChange = handleChange || onChange;

  const emitChange = (selected) => {
    if (typeof handleOnChange !== "function") {
      return;
    }

    handleOnChange({
      target: {
        name,
        value: selected ? selected.value : "",
      },
    });
  };

  const ErrorMessage = () =>
    hasError ? (
      <span className="mt-1 text-[10px] text-cs-danger">{error}</span>
    ) : null;

  const portalTarget =
    typeof window !== "undefined" && typeof document !== "undefined" && document.body
      ? document.body
      : null;

  const customStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "2.25rem",
      borderRadius: "0.5rem",
      borderColor: hasError ? "#ef4444" : state.isFocused ? "#2563eb" : "#d8dee7",
      backgroundColor: colorOption || (isDisabled ? "#f4f5f5" : "#f8fafc"),
      boxShadow: state.isFocused ? "0 0 0 2px rgba(37, 99, 235, 0.2)" : "none",
      cursor: isDisabled ? "not-allowed" : "default",
      opacity: isDisabled ? 0.6 : 1,
      transition: "all 150ms ease",
      "&:hover": {
        borderColor: hasError ? "#ef4444" : "#2563eb",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 0.75rem",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#111827",
      fontSize: "0.75rem",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#7b8794",
      fontSize: "0.75rem",
    }),
    input: (base) => ({
      ...base,
      color: "#111827",
      fontSize: "0.75rem",
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      minHeight: "2.25rem",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "0 0.5rem",
      color: "#7b8794",
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: "0 0.5rem",
      color: "#7b8794",
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: "#d8dee7",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      borderRadius: "0.75rem",
      overflow: "hidden",
      boxShadow: "0 18px 45px rgba(15, 23, 42, 0.16)",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "0.75rem",
      cursor: "pointer",
      color: state.isSelected ? "#ffffff" : "#111827",
      backgroundColor: state.isSelected
        ? "#2563eb"
        : state.isFocused
          ? "rgba(37, 99, 235, 0.08)"
          : "#ffffff",
      "&:active": {
        backgroundColor: state.isSelected ? "#2563eb" : "rgba(37, 99, 235, 0.14)",
      },
    }),
  };

  return (
    <div className={`flex flex-col w-full py-1 gap-1 ${className}`}>
      {label && <label className={labelClassName}>{label}</label>}

      <Select
        name={name}
        options={formattedOptions}
        value={selectedOption}
        onChange={emitChange}
        isDisabled={isDisabled}
        placeholder={holder ?? "Select"}
        styles={customStyles}
        menuPortalTarget={portalTarget}
        classNamePrefix="cs-select"
        {...props}
      />

      <ErrorMessage />
    </div>
  );
};

export default CustomSelect;
